/**
 * ForgeDetailPage
 *
 * Page displaying detailed information about a single Forge.
 */
import { ArrowLeft, Server } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageLayout } from '../../components/Layout/PageLayout';
import { Button } from '../../components/Common/button';
import { Badge } from '../../components/Common/badge';
import { OneForgeModule } from '../../components/Forge';
import forgeService from '../../services/forgeService';
import { ForgeWithDomain } from '../../types';

// Helper to capitalize first letter
const capitalizeFirst = (str?: string | null): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Helper to get status badge variant
const getStatusBadgeVariant = (status?: string): 'default' | 'secondary' | 'destructive' | 'outline' => {
  if (!status) return 'outline';
  switch (status.toLowerCase()) {
    case 'deployed':
      return 'default';
    case 'creating':
    case 'deploying':
    case 'updating':
      return 'secondary';
    case 'error':
    case 'deleting':
      return 'destructive';
    default:
      return 'outline';
  }
};

const ForgeDetailPage: React.FC = () => {
  const { forgeId } = useParams<{ forgeId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [forgeData, setForgeData] = useState<ForgeWithDomain | null>(null);

  useEffect(() => {
    // Try to find the forge with linked domain info
    const fetchForgeData = async () => {
      if (!forgeId) return;
      try {
        // Get forges with domains and find this one
        const forgesWithDomains = await forgeService.listForgesWithDomains();
        const forge = forgesWithDomains.find(f => f.forge_id === forgeId);
        if (forge) {
          setForgeData(forge);
        }
      } catch (err) {
        console.error('Error fetching forge data:', err);
      }
    };

    fetchForgeData();
  }, [forgeId]);

  const handleApplicationClick = (appId: string) => {
    navigate(`/applications/${appId}`);
  };

  if (!forgeId) {
    return (
      <PageLayout icon={<Server className="h-6 w-6" />} title={t('forge.pages.forgesList', 'Forges')} subtitle={t('forge.detail.invalidId', 'Invalid Forge ID')}>
        <p className="text-destructive">{t('forge.detail.invalidId', 'Invalid Forge ID')}</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      icon={<Server className="h-6 w-6" />}
      title={capitalizeFirst(forgeData?.display_name || forgeData?.name) || t('forge.pages.forgeDetail', 'Forge Detail')}
      subtitle={t('forge.detail.subtitle', 'View Forge details and manage applications')}
      titleSuffix={forgeData?.status ? <Badge variant={getStatusBadgeVariant(forgeData.status)} className="ml-2">{forgeData.status}</Badge> : undefined}
      headerRight={
        <Button variant="outline" onClick={() => navigate('/forges')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('forge.detail.backToList', 'Back to Forges')}
        </Button>
      }
      subtitleExtra={
        forgeData?.domain_id && forgeData?.domain_name ? (
          <p className="text-sm mt-1">
            <Link to={`/domains/${forgeData.domain_id}`} className="text-primary hover:underline">
              {forgeData.domain_name}
            </Link>
          </p>
        ) : undefined
      }
    >
      <div className="space-y-6">
        <OneForgeModule
          forgeId={forgeId}
          domainId={forgeData?.domain_id}
          domainName={forgeData?.domain_name}
          showHeader={false}
          hideDomainLink={true}
          showOverview={true}
          showApplicationsList={true}
          showServices={true}
          showLogs={true}
          onApplicationClick={handleApplicationClick}
        />
      </div>
    </PageLayout>
  );
};

export default ForgeDetailPage;
